// src/components/Privacy.js
//
// Privacy policy + the messaging terms the /alerts form links to.
//
// Everything here describes what the system ACTUALLY does today. If the text
// signup changes (what it collects, who delivers the texts, how long records are
// kept), change this page in the same commit — a privacy policy that has drifted
// from the code is worse than none, because people relied on it.
//
// The consent wording on /alerts is versioned (CONSENT_TEXT_VERSION in Alerts.js)
// and every signup records which version it agreed to. If the meaning of what
// somebody is agreeing to changes, bump that version too.

import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

// Shown at the top so a reader can tell whether this is current.
const LAST_UPDATED = 'September 24, 2026';

const Privacy = () => {
  return (
    <div className="privacy-container">
      <h1>Privacy &amp; Text Message Terms</h1>
      <p className="privacy-updated">Last updated: {LAST_UPDATED}</p>

      <p>
        This page explains what Capital Pinball Parlor collects, why, and what you can do
        about it. The short version: we collect what we need to text you about pinball, we
        do not sell it, and you can stop at any time by replying STOP.
      </p>

      {/* ---------------- Messaging terms ---------------- */}
      <h2 id="text-alerts">Text message alerts</h2>

      <p>
        If you sign up on our{' '}
        <Link to="/alerts">alerts page</Link>, you are agreeing to receive text messages
        from Capital Pinball Parlor about pinball events and tournaments.
      </p>

      <ul>
        <li>
          <strong>What you get:</strong> announcements about events, tournaments and
          league nights. If you tick the women&apos;s events box, you also get alerts about
          those specifically.
        </li>
        <li>
          <strong>How often:</strong> message frequency varies. There is no fixed
          schedule &mdash; we text when something is happening.
        </li>
        <li>
          <strong>Cost:</strong> we do not charge for alerts, but message and data rates
          may apply from your mobile carrier.
        </li>
        <li>
          <strong>To stop:</strong> reply <strong>STOP</strong> to any message. You will
          get one confirmation and then nothing further. You can also reply UNSUBSCRIBE,
          CANCEL, END or QUIT.
        </li>
        <li>
          <strong>To get help:</strong> reply <strong>HELP</strong> to any message and we
          will text back who we are and how to reach us.
        </li>
        <li>
          <strong>To rejoin:</strong> reply <strong>START</strong>, or sign up again on the
          alerts page.
        </li>
      </ul>

      <p>
        Mobile carriers are not liable for delayed or undelivered messages. Alerts are
        available on most major US carriers.
      </p>

      {/* ---------------- What we collect ---------------- */}
      <h2 id="what-we-collect">What we collect</h2>

      <p>When you sign up for text alerts we collect:</p>
      <ul>
        <li>your first and last name</li>
        <li>your mobile number</li>
        <li>whether you want women&apos;s event alerts</li>
        <li>
          the date and time you signed up, your IP address, your browser&apos;s user agent,
          and the exact wording of the consent you agreed to
        </li>
      </ul>

      <p>
        That last group exists for one reason: text messaging rules require us to be able
        to show that you asked to hear from us. It is kept as a record of your consent, not
        used for advertising or profiling.
      </p>

      <p>
        Browsing the rest of this site does not require an account, and we do not run
        advertising trackers on it. Our hosting provider keeps standard server logs.
      </p>

      {/* ---------------- Who else sees it ---------------- */}
      <h2 id="who-sees-it">Who else sees it</h2>

      <p>
        <strong>We do not sell, rent or trade your information.</strong> We share it only
        with the services that make the alerts work:
      </p>
      <ul>
        <li>
          <strong>Twilio</strong> &mdash; delivers the text messages. They receive your
          mobile number and the message.
        </li>
        <li>
          <strong>Cloudflare</strong> &mdash; hosts this website and runs the &ldquo;confirm
          you are a person&rdquo; check on the signup form, which is there to keep bots from
          signing up numbers that never asked.
        </li>
      </ul>

      <p>
        We may also disclose information if the law requires it, or to protect the safety of
        our customers and staff.
      </p>

      {/* ---------------- How long ---------------- */}
      <h2 id="how-long">How long we keep it</h2>

      <p>
        We keep your name and number for as long as you are subscribed. When you reply STOP
        we stop texting you immediately.
      </p>

      <p>
        We keep the record of your original sign-up &mdash; the date, and what you agreed to
        &mdash; even after you unsubscribe. That record is what proves you were on the list
        legitimately, and we would need it if a mistake or a complaint ever had to be sorted
        out. If you would rather we deleted everything, ask us and we will.
      </p>

      {/* ---------------- Your choices ---------------- */}
      <h2 id="your-choices">Your choices</h2>
      <ul>
        <li>Reply STOP to any message to stop receiving texts.</li>
        <li>Reply HELP to any message to reach us.</li>
        <li>Call us to ask what we hold about you, to correct it, or to have it deleted.</li>
      </ul>

      {/* ---------------- Other ---------------- */}
      <h2 id="children">Children</h2>
      <p>
        The text alert programme is not directed at children under 13, and we do not
        knowingly sign them up. If you believe a child&apos;s number was submitted, contact
        us and we will remove it.
      </p>

      <h2 id="security">Keeping it safe</h2>
      <p>
        Sign-ups are sent over an encrypted connection and stored in access-controlled
        systems. No system is perfectly secure, but we do not keep anything here we do not
        need &mdash; there are no payment details or passwords in the alerts list.
      </p>

      <h2 id="changes">Changes to this page</h2>
      <p>
        If we change what we collect or how we use it, we will update this page and the date
        at the top. If the change affects what you agreed to when you signed up, we will ask
        you again rather than assume.
      </p>

      <h2 id="contact">Contact us</h2>
      <p>
        Capital Pinball Parlor
        <br />
        Sacramento, California
        <br />
        Phone: <a href="tel:+19169139170">(916) 913-9170</a>
        <br />
        Web: <a href="https://www.capitalpinballparlor.com">capitalpinballparlor.com</a>
      </p>
    </div>
  );
};

export default Privacy;
